import { useMemo, useState, useCallback, useEffect } from 'react';

import { GoalDto, useListGoals } from '@/api/generated';
import useLoginContext from '@/context/LoginFlowContextProvider/useLoginContext';
import {
  getCurrentQuarterAndYear,
  getQuarterTimestamps,
  getYearAndQuarter,
  QuarterYear
} from '@/utils/dateUtil';

const useGoalsLogic = () => {
  const { currentAccount } = useLoginContext();
  const monthToStart = currentAccount?.config?.fiscalYearStartMonth || 1;

  const currentQuarterYear = getCurrentQuarterAndYear(monthToStart);
  const [selectedPeriod, setSelectedPeriod] = useState<QuarterYear>(currentQuarterYear);
  const [selectedGoals, setSelectedGoals] = useState<GoalDto[]>([]);
  const [dueDate, setDueDate] = useState<string | undefined>();

  const {
    data: goalListResponse,
    refetch: refetchGoals
    // isLoading: goalListLoading
  } = useListGoals();

  const goalList = useMemo(() => goalListResponse?.items || [], [goalListResponse]);

  const periods = useMemo(() => {
    const periodList: QuarterYear[] = [];

    if (goalList?.length > 0) {
      goalList.forEach(goal => {
        if (goal.dueDate) {
          const quarterYear = getYearAndQuarter(new Date(goal.dueDate), monthToStart);
          if (
            !periodList.some(
              period => period.quarter === quarterYear.quarter && period.year === quarterYear.year
            )
          ) {
            periodList.push(quarterYear);
          }
        }
      });
    }

    // Add remaining quarters from current year
    for (let quarter = currentQuarterYear.quarter; quarter <= 4; quarter++) {
      if (
        !periodList.some(
          period => period.quarter === quarter && period.year === currentQuarterYear.year
        )
      ) {
        periodList.push({ quarter: quarter, year: currentQuarterYear.year });
      }
    }

    // Add all quarters from next year
    const nextYear = currentQuarterYear.year + 1;
    for (let quarter = 1; quarter <= 4; quarter++) {
      if (!periodList.some(period => period.quarter === quarter && period.year === nextYear)) {
        periodList.push({ quarter: quarter, year: nextYear });
      }
    }

    // Sort periods by year and quarter
    return periodList.sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      return a.quarter - b.quarter;
    });
  }, [goalList, monthToStart, currentQuarterYear]);

  useEffect(() => {
    const selectedTimestamps = getQuarterTimestamps(
      selectedPeriod.year,
      selectedPeriod.quarter,
      monthToStart
    );
    setDueDate(new Date(selectedTimestamps.to).toISOString());

    setSelectedGoals(
      goalList?.filter(goal => {
        if (goal.dueDate) {
          const goalTimestamp = new Date(goal.dueDate).getTime();
          return goalTimestamp >= selectedTimestamps.from && goalTimestamp <= selectedTimestamps.to;
        }
        return false;
      })
    );
  }, [selectedPeriod, monthToStart, goalList, refetchGoals]);

  const sortedGoals = useMemo(
    () =>
      selectedGoals.toSorted(
        (a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
      ),
    [selectedGoals]
  );

  return {
    periods,
    selectedPeriod,
    setSelectedPeriod,
    selectedGoals,
    monthToStart,
    sortedGoals,
    dueDate,
    refetchGoals
  };
};

export default useGoalsLogic;
